'use client';

import { useEffect, useRef, useState } from 'react';
import { PROBE_LABEL } from './data';
import { LATENCY_WINDOW, type StatusSnapshot } from '@/lib/status/model';
import LatencyPlot from './LatencyPlot';
import UpdatedAgo from './UpdatedAgo';
import { SHEET, SheetHeader } from './Sheet';

const POLL_MS = 60_000;
const PLOT_ROWS = 3;

/**
 * The latency sheet: one white clearing on the grid, the same chrome as the
 * components sheet below it (one-row header bar, hairline on the row line).
 * On xl the body is two columns — the readouts in a five-cell column on the
 * left, the plot window on the right, its edges on page grid lines with a
 * half-cell gutter on the left for the y labels and a cell and a half on the
 * right for the reference labels. Below xl the readouts stack above the plot.
 * Sheet height is a fixed number of rows in both layouts, so the block always
 * ends on a row line and the next block's free row is exact.
 *
 * Polls /api/status once a minute while the tab is visible; the server
 * snapshot is the initial state, so nothing here needs JS to be true.
 */
export default function LatencyPanel({ initial }: { initial: StatusSnapshot }) {
  const [snap, setSnap] = useState(initial);
  /*
   * Keeping the last good reading on screen is right — a blank panel during a
   * blip helps nobody — but silently freezing it is a lie. So we remember WHEN
   * the readings stopped refreshing and say so, and clear it the moment a real
   * snapshot lands. Staleness is armed only by a good snapshot: a page that
   * arrives already unavailable has nothing that went stale.
   */
  const [staleSince, setStaleSince] = useState<string | null>(null);
  const hadGood = useRef(initial.source === 'kuma');

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (document.hidden) return;
      const wentStale = () => {
        if (!cancelled && hadGood.current) setStaleSince((prev) => prev ?? new Date().toISOString());
      };
      try {
        const res = await fetch('/api/status', { cache: 'no-store' });
        if (!res.ok) return wentStale();
        const next = (await res.json()) as StatusSnapshot;
        if (cancelled) return;
        if (next.source !== 'kuma') return wentStale();
        hadGood.current = true;
        setSnap(next);
        setStaleSince(null);
      } catch {
        wentStale();
      }
    };
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const { p50, p99, n, series } = snap.latency;

  return (
    <section
      aria-labelledby="latency-h"
      data-grid-block
      data-grid-fill="#ffffff"
      className={`${SHEET} md:h-[calc(var(--grid-cell,72px)*7)] xl:h-[calc(var(--grid-cell,72px)*5)]`}
    >
      <SheetHeader
        id="latency-h"
        title="ROUTING LATENCY · LAST HOUR"
        note={
          <>
            {n} SAMPLES · <UpdatedAgo iso={snap.updatedAt} />
          </>
        }
      />

      <div className="grid md:h-[calc(var(--grid-cell,72px)*6)] xl:h-[calc(var(--grid-cell,72px)*4)] xl:grid-cols-[calc(var(--grid-cell,72px)*5)_minmax(0,1fr)]">
        {/* Readouts column. */}
        <div className="flex flex-col justify-center px-5 py-6 md:h-[calc(var(--grid-cell,72px)*2)] xl:h-auto xl:pr-0">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <span className="flex items-baseline gap-2">
              <Readout v={p50} size="text-[clamp(44px,5vw,64px)] tracking-[-0.04em]" dash="text-[clamp(26px,3vw,32px)]" />
              <span className="font-jetbrains text-[14px] text-muted">ms p50</span>
            </span>
            <span className="flex items-baseline gap-2">
              <Readout v={p99} size="text-[clamp(22px,2.2vw,26px)] tracking-[-0.03em]" dash="text-[20px]" />
              <span className="font-jetbrains text-[12.5px] text-muted">ms p99</span>
            </span>
          </div>
          <p className="mt-3 max-w-[38ch] text-[12.5px] leading-[1.5] text-muted">{PROBE_LABEL}</p>
          {staleSince && (
            <p className="mt-2 font-jetbrains text-[11px] text-data-amber">
              Status data <UpdatedAgo iso={staleSince} prefix="unavailable since" className="text-data-amber" />
            </p>
          )}
        </div>

        {/* Plot column: the window sits directly under the header rule (its
            top line IS the rule, so its rows coincide with page rows), then one
            row of time labels. Left gutter half a cell (y labels), right gutter
            a cell and a half (reference labels); the box's edges therefore land
            on page grid lines in both axes and both layouts. */}
        <div className="px-5 pb-6 md:px-0 md:pl-[calc(var(--grid-cell,72px)/2)] md:pr-[calc(var(--grid-cell,72px)*1.5)] md:pb-0">
          <LatencyPlot series={series} rows={PLOT_ROWS} window={LATENCY_WINDOW} p50={p50} p99={p99} />
        </div>
      </div>
    </section>
  );
}

/**
 * A missing reading is an em dash, and an em dash at the display size is a
 * fat black bar that reads as a design element rather than an absence. The
 * dash gets a smaller, dimmer treatment; the unit label stays so the row
 * still says what is missing.
 */
function Readout({ v, size, dash }: { v: number | null; size: string; dash: string }) {
  return v === null ? (
    <span className={`font-onest ${dash} font-semibold leading-none text-dim`}>—</span>
  ) : (
    <span className={`font-onest ${size} font-extrabold leading-none text-ink tabular-nums`}>{Math.round(v)}</span>
  );
}
